import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Col, Icon, InfoBox, Row, useUI } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataDepartmentAgreement, APIDataAgreementDocument } from '@evertel/types'
import { AgreementController } from '@evertel/agreements'
import { AgreementCard, AgreementCardPlaceholder } from './AgreementCard'
import { CreateAgreementModal } from '../modals/CreateAgreementModal'
import { EditAgreementModal } from '../modals/EditAgreementModal'
import { AgreementPreviewModal } from '../modals/AgreementPreviewModal'
import { AgreementVersionHistoryModal } from '../modals/AgreementVersionHistoryModal'

const AgencyAgreementsSection: React.FC = observer(() => {
    const sessionService = useService(SessionService, [])
    const departmentId = sessionService.selectedDepartmentId
    const agreementController = useService(AgreementController, [departmentId])
    const { addToast } = useUI()

    const [isLoading, setIsLoading] = useState(false)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editingAgreement, setEditingAgreement] = useState<APIDataDepartmentAgreement | null>(null)
    const [previewDocument, setPreviewDocument] = useState<APIDataAgreementDocument | null>(null)
    const [versionHistoryAgreement, setVersionHistoryAgreement] = useState<APIDataDepartmentAgreement | null>(null)

    useEffect(() => {
        if (!departmentId) return
        agreementController.init(departmentId)
        refresh()
    }, [departmentId])

    const refresh = () => {
        setIsLoading(true)
        agreementController.fetchAgreements().finally(() => setIsLoading(false))
    }

    const handleDelete = async (agreement: APIDataDepartmentAgreement) => {
        if (!agreement.id) return

        try {
            await agreementController.deleteAgreement(agreement.id)
            addToast({ color: 'success', title: 'Agreement Deleted', message: 'The agreement has been deleted.' })
        } catch (e: any) {
            addToast({ color: 'danger', title: 'Delete Unsuccessful', message: e?.message || 'Failed to delete agreement' })
        }
    }

    return (
        <>
            <Row valign="center" className="mb-3">
                <Col xs={11} className="me-2">
                    <strong className="d-block mb-1">Agency Agreements</strong>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Set rules for users to acknowledge documents such as Memorandums of Understanding, Rules of Behavior, or anything that meets your agency's needs.
                    </span>
                </Col>
                <Col>
                    <Button
                        color="primary"
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        Add
                    </Button>
                </Col>
            </Row>

            {isLoading && agreementController.agreements.length === 0 && (
                <>
                    <AgreementCardPlaceholder />
                    <AgreementCardPlaceholder />
                </>
            )}

            {!isLoading && agreementController.agreements.length === 0 && (
                <InfoBox color="info" className="mt-2" style={{ color: 'currentColor' }}>
                    <Icon name="circle-info" type="solid" color="blue" className="me-2" />
                    There are no agreements for your agency yet
                </InfoBox>
            )}

            {agreementController.agreements.map(agreement => {
                if (!agreement.id) return null

                return (
                    <AgreementCard
                        key={agreement.id}
                        agreement={agreement}
                        canDelete={!agreementController.agreementIdsWithAcceptances.has(agreement.id)}
                        onEdit={setEditingAgreement}
                        onPreview={(a) => setPreviewDocument(a.currentDocument ?? null)}
                        onVersionHistory={setVersionHistoryAgreement}
                        onDelete={handleDelete}
                    />
                )
            })}

            <CreateAgreementModal
                visible={isCreateModalOpen}
                onSuccess={refresh}
                onClose={() => setIsCreateModalOpen(false)}
            />

            {editingAgreement && (
                <EditAgreementModal
                    visible={!!editingAgreement}
                    agreement={editingAgreement}
                    onSuccess={refresh}
                    onClose={() => setEditingAgreement(null)}
                />
            )}

            {previewDocument && (
                <AgreementPreviewModal
                    visible={!!previewDocument}
                    document={previewDocument}
                    onClose={() => setPreviewDocument(null)}
                />
            )}

            {versionHistoryAgreement?.id && departmentId && (
                <AgreementVersionHistoryModal
                    visible={!!versionHistoryAgreement}
                    agreementId={versionHistoryAgreement.id}
                    departmentId={departmentId}
                    agreementTitle={versionHistoryAgreement.currentDocument?.name ?? versionHistoryAgreement.title ?? ''}
                    currentDocumentId={versionHistoryAgreement.agreementDocumentId}
                    onClose={() => setVersionHistoryAgreement(null)}
                />
            )}
        </>
    )
})

AgencyAgreementsSection.displayName = 'AgencyAgreementsSection'
export { AgencyAgreementsSection }
